/**
 * "Rejected" — why an RM or Admin rejected this plan back to its author.
 *
 * Shown while the plan's Resourcing status is Returned (shown as Rejected). The plan's own
 * status (Open, Proposed, Won…) is untouched; the server clears the rejection when the
 * author submits the plan to Resourcing again, the RM moves the Resourcing status off it,
 * or the RM resolves the Blocking Staffing Concern that rejected it (RP-34) — the Concern
 * is where the account team answers.
 */

import { Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

export function PlanReturnedBanner({
  plan,
  onViewConcern,
}: {
  plan: ResourcePlanDetailView;
  onViewConcern: () => void;
}) {
  const rejection = plan.rejection;
  if (rejection == null || plan.resourcingStatus !== "Returned") return null;

  const when = new Date(rejection.at).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-md border border-warning-200 bg-warning-100 px-3 py-2.5 text-sm text-warning-950 dark:text-warning-400"
    >
      <Undo2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="space-y-0.5">
        <p className="font-medium">
          Rejected{rejection.byName === null ? "" : ` by ${rejection.byName}`} on {when}
        </p>
        <p className="whitespace-pre-wrap">{rejection.reason}</p>
        <p className="text-xs text-muted-foreground">
          The plan's status is unchanged. Acknowledge the rejection, make the changes, then submit
          the plan to Resourcing again.
        </p>
      </div>
      <Button size="sm" variant="outline" className="ml-auto shrink-0" onClick={onViewConcern}>
        View rejection
      </Button>
    </div>
  );
}
